import { ChevronRight, Zap } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EventStatusBadge, SeverityBadge } from "@/components/common/SeverityBadge";
import { durationLabel, fmtDayTime, fmtRelative, fmtTime, toOslo } from "@/lib/format";
import { SEVERITY } from "@/lib/status";
import { cn } from "@/lib/utils";

/* Whether the event is still going on, or when it ended. This is what reconciles «Redusert drift» in the event list
   with «I drift» in the plant list: the event has ended and only waits for acknowledgement. */
const sameDay = (a, b) => {
  const x = toOslo(a);
  const y = toOslo(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
};
export const endedLabel = (e) => {
  if (!e.ended_at) return "pågår";
  return `avsluttet ${sameDay(e.started_at, e.ended_at) ? `kl. ${fmtTime(e.ended_at)}` : fmtDayTime(e.ended_at)}`;
};

/** Responsive events list: table on desktop, stacked rows on mobile. `flush` = inside a panel (edge padding);
 *  otherwise the columns start at the page edge, like the plant table. */
export const EventsTable = ({ events = [], onSelect, showPlant = true, compact = false, flush = false, testId = "hendelser-tabell" }) => {
  if (!events.length) return null;
  const edge = flush ? "pl-5" : "pl-0";
  const edgeR = flush ? "pr-5" : "pr-0";
  return (
    <>
      {/* Mobile rows */}
      <ul className={cn("hairline-y md:hidden", flush && "border-t border-border")} data-testid={`${testId}-mobile`}>
        {events.map((e) => {
          const sev = SEVERITY[e.severity] || SEVERITY.info;
          return (
            <li key={e.id}>
              <button type="button" onClick={() => onSelect?.(e)} className={cn("row-link flex w-full items-start gap-3 py-3 text-left", flush ? "px-5" : "px-0")} data-testid={`event-row-${e.id}`}>
                <span className={cn("status-dot mt-[7px]", sev.dotClass)} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-medium leading-snug">{e.title}</div>
                  <div className="mono mt-1 text-[10.5px] text-muted-foreground">
                    {showPlant && e.plant_name ? `${e.plant_name} · ` : ""}
                    {fmtDayTime(e.started_at)} · {durationLabel(e.duration_h)} · <span className={cn(!e.ended_at && e.status !== "resolved" && "font-medium text-foreground")}>{endedLabel(e)}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2.5">
                    <SeverityBadge severity={e.severity} className="text-[11px]" />
                    <EventStatusBadge status={e.status} />
                  </div>
                </div>
                <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground/40" aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
      {/* Desktop table */}
      <div className="hidden md:block">
        <Table data-testid={testId}>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={cn("w-[110px]", edge)}>Alvor</TableHead>
              <TableHead>Hendelse</TableHead>
              {showPlant ? <TableHead>Kraftverk</TableHead> : null}
              <TableHead className="w-[170px]">Start</TableHead>
              {!compact ? <TableHead className="w-[150px]">Varighet</TableHead> : null}
              <TableHead className="w-[110px]">Status</TableHead>
              <TableHead className={cn("w-[36px]", edgeR)} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map((e) => (
              <TableRow key={e.id} onClick={() => onSelect?.(e)} className="group cursor-pointer" data-testid={`event-row-${e.id}`}>
                <TableCell className={edge}>
                  <SeverityBadge severity={e.severity} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 font-medium">
                    {e.title}
                    {e.production_stop ? <Zap className="h-3 w-3 text-amber-600 dark:text-amber-300" aria-label="Produksjonsstans" /> : null}
                  </div>
                  <div className="text-[11.5px] text-muted-foreground">{e.kind_label}</div>
                </TableCell>
                {showPlant ? <TableCell className="text-[13px]">{e.plant_name}</TableCell> : null}
                <TableCell>
                  <div className="mono text-[11.5px]">{fmtDayTime(e.started_at)}</div>
                  <div className="text-[11px] text-muted-foreground">{fmtRelative(e.started_at)}</div>
                </TableCell>
                {!compact ? (
                  <TableCell>
                    <div className="metric text-[12.5px]">{durationLabel(e.duration_h)}</div>
                    <div className={cn("whitespace-nowrap text-[11px]", !e.ended_at ? "font-medium text-foreground" : "text-muted-foreground")} data-testid={`event-ended-${e.id}`}>{endedLabel(e)}</div>
                  </TableCell>
                ) : null}
                <TableCell>
                  <EventStatusBadge status={e.status} />
                </TableCell>
                <TableCell className={edgeR}>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/40 transition-colors group-hover:text-foreground" aria-hidden="true" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
};
